/* ==========================================================================
   THE GUILD — Theme JS
   ========================================================================== */

(function() {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  // --------------------------------------------------------------------------
  // Drawer system (cart + menu)
  // --------------------------------------------------------------------------
  const Drawer = {
    open(drawer) {
      drawer.classList.add('is-open');
      $('#overlay').classList.add('is-active');
      document.body.classList.add('no-scroll');
      // Focus trap
      const firstFocusable = drawer.querySelector('button, a, input');
      if (firstFocusable) firstFocusable.focus();
    },
    close(drawer) {
      drawer.classList.remove('is-open');
      // Only deactivate overlay if no other drawer is open
      if (!$('.is-open[data-drawer]')) {
        $('#overlay').classList.remove('is-active');
        document.body.classList.remove('no-scroll');
      }
    },
    closeAll() {
      $$('[data-drawer]').forEach(d => d.classList.remove('is-open'));
      $('#overlay').classList.remove('is-active');
      document.body.classList.remove('no-scroll');
    }
  };

  // Bind drawer triggers
  document.addEventListener('click', (e) => {
    const openTrigger = e.target.closest('[data-drawer-open]');
    if (openTrigger) {
      e.preventDefault();
      const target = $(`[data-drawer="${openTrigger.dataset.drawerOpen}"]`);
      if (target) Drawer.open(target);
    }

    const closeTrigger = e.target.closest('[data-drawer-close]');
    if (closeTrigger) {
      const parentDrawer = closeTrigger.closest('[data-drawer]');
      if (parentDrawer) Drawer.close(parentDrawer);
    }

    if (e.target.closest('[data-overlay]')) Drawer.closeAll();
  });

  // ESC to close drawers
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Drawer.closeAll();
  });

  // Swipe to close drawers (mobile)
  let touchStartX = 0;
  document.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX;
  }, { passive: true });

  document.addEventListener('touchend', (e) => {
    const openDrawer = $('[data-drawer].is-open');
    if (!openDrawer) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (openDrawer.classList.contains('menu-drawer') && deltaX < -50) Drawer.close(openDrawer);
    if (openDrawer.classList.contains('cart-drawer') && deltaX > 50) Drawer.close(openDrawer);
  }, { passive: true });

  // --------------------------------------------------------------------------
  // Accordions (PDP, footer on mobile)
  // --------------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    const item = trigger.closest('[data-accordion-item]');
    if (!item) return;
    item.classList.toggle('is-open');
    const icon = trigger.querySelector('[data-accordion-icon]');
    if (icon) icon.textContent = item.classList.contains('is-open') ? '−' : '+';
  });

  // --------------------------------------------------------------------------
  // Quantity steppers (cart)
  // --------------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const action = btn.dataset.qty;
    const input = btn.parentElement.querySelector('[data-qty-value]');
    if (!input) return;
    let value = parseInt(input.dataset.qtyValue || input.value, 10) || 0;
    if (action === 'plus') value++;
    if (action === 'minus' && value > 1) value--;
    input.textContent = value;
    input.dataset.qtyValue = value;
    const line = btn.dataset.line;
    if (line) Cart.updateLine(line, value);
  });

  // --------------------------------------------------------------------------
  // Cart (Shopify AJAX API)
  // --------------------------------------------------------------------------
  const Cart = {
    async add(formData) {
      // Shopify /cart/add.js expects { items: [{ id, quantity }] } for JSON,
      // OR a flat form object as URL-encoded data. We use the items[] shape.
      const id = formData.id || formData.variant_id;
      const quantity = parseInt(formData.quantity || 1, 10);
      if (!id) throw new Error('No variant ID supplied');

      const payload = {
        items: [{ id: parseInt(id, 10), quantity }]
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error('Cart add failed:', res.status, errText);
        throw new Error('Add to cart failed');
      }
      await this.refresh();
      Drawer.open($('[data-drawer="cart"]'));
    },
    async updateLine(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity })
      });
      if (res.ok) await this.refresh();
    },
    async refresh() {
      try {
        // Section Rendering API: fetch ONLY the cart-drawer section's HTML.
        // This works because cart-drawer is now a section (sections/cart-drawer.liquid),
        // rendered in the layout as {% section 'cart-drawer' %}.
        const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        if (!res.ok) throw new Error('Section fetch failed');
        const text = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        // The response is the section's inner content. Find the drawer element.
        const newDrawer = doc.querySelector('[data-drawer="cart"]');
        const oldDrawer = $('[data-drawer="cart"]');
        if (newDrawer && oldDrawer) {
          const wasOpen = oldDrawer.classList.contains('is-open');
          oldDrawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen) {
            oldDrawer.classList.add('is-open');
            oldDrawer.setAttribute('aria-hidden', 'false');
          }
        }
        await this.updateCount();
      } catch (err) { console.error('Cart refresh error:', err); }
    },
    async updateCount() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        $$('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.toggleAttribute('hidden', cart.item_count === 0);
        });
      } catch (err) { console.error(err); }
    }
  };

  // Add-to-cart forms
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-add-to-cart-form]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const textEl = submitBtn ? submitBtn.querySelector('[data-add-button-text]') : null;
    const originalLabel = textEl ? textEl.textContent : (submitBtn ? submitBtn.textContent : '');

    if (submitBtn) submitBtn.disabled = true;
    if (textEl) textEl.textContent = 'ADDING…';
    else if (submitBtn) submitBtn.textContent = 'ADDING…';

    try {
      const formData = new FormData(form);
      const items = {};
      formData.forEach((value, key) => { items[key] = value; });
      await Cart.add(items);
      // After successful add, briefly show success
      if (textEl) textEl.textContent = 'ADDED ✓';
      else if (submitBtn) submitBtn.textContent = 'ADDED ✓';
      setTimeout(() => {
        if (textEl) textEl.textContent = originalLabel;
        else if (submitBtn) submitBtn.textContent = originalLabel;
        if (submitBtn) submitBtn.disabled = false;
      }, 1000);
    } catch (err) {
      console.error(err);
      if (textEl) textEl.textContent = originalLabel;
      else if (submitBtn) submitBtn.textContent = originalLabel;
      if (submitBtn) submitBtn.disabled = false;
      alert('We could not add this piece. Please refresh and try again.');
    }
  });

  // Remove item buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    await Cart.updateLine(btn.dataset.cartRemove, 0);
  });

  // --------------------------------------------------------------------------
  // Product page — variant selection (proper resolver)
  // --------------------------------------------------------------------------
  function initProductForm() {
    const container = $('[data-product-container]');
    if (!container) return;

    const form = container.querySelector('[data-add-to-cart-form]');
    if (!form) return;

    const jsonNode = container.querySelector('[data-product-json]');
    if (!jsonNode) return;

    let productData;
    try {
      productData = JSON.parse(jsonNode.textContent);
    } catch (e) {
      console.error('Bad product JSON', e);
      return;
    }
    const variants = productData.variants || [];

    const variantInput = form.querySelector('[data-product-variant-id]');
    const priceEl = container.querySelector('[data-product-price]');
    const stockNote = container.querySelector('[data-stock-note]');
    const addButton = form.querySelector('[data-add-button]');
    const addButtonText = form.querySelector('[data-add-button-text]');
    const colorSelectedLabel = container.querySelector('[data-color-selected]');

    // Format money — uses Shopify money format from window if available, else basic GBP
    const money = (cents) => {
      const value = (cents / 100).toFixed(2);
      const symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'GBP') ? '£' :
                     productData.price_min !== undefined ? '£' : '£';
      return symbol + value;
    };

    // Find variant matching current option selections
    function getSelectedOptions() {
      const opts = {};
      form.querySelectorAll('input[name^="options["]:checked').forEach(input => {
        const match = input.name.match(/options\[(.+?)\]/);
        if (match) opts[match[1]] = input.value;
      });
      return opts;
    }

    function findMatchingVariant() {
      const selected = getSelectedOptions();
      const optionNames = productData.options || [];
      return variants.find(v => {
        return optionNames.every((optName, i) => {
          const selectedValue = selected[optName];
          if (selectedValue === undefined) return true; // option not yet selected
          return v[`option${i + 1}`] === selectedValue;
        });
      });
    }

    function update() {
      // Update visual selected states for swatches and size buttons
      form.querySelectorAll('input[name="options[Color]"]').forEach(input => {
        const label = input.closest('.color-swatch') || form.querySelector(`[data-color-value="${input.value}"]`);
        if (label) label.classList.toggle('is-selected', input.checked);
      });

      form.querySelectorAll('input[name="options[Size]"]').forEach(input => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-selected', input.checked);
      });

      // Update color selected label
      const colorInput = form.querySelector('input[name="options[Color]"]:checked');
      if (colorInput && colorSelectedLabel) {
        colorSelectedLabel.textContent = colorInput.value.toUpperCase();
      }

      // Find the matching variant
      const variant = findMatchingVariant();

      if (variant) {
        // Update hidden ID
        if (variantInput) variantInput.value = variant.id;

        // Update URL (?variant=...)
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        // Update price
        if (priceEl) priceEl.textContent = money(variant.price);

        // Update button state.
        // Only disable when inventory tracking confirms the variant is out of stock.
        // If tracking is off (inventory_management is null), trust Shopify's `available` flag
        // but still allow the click — the cart endpoint will reject if truly unavailable.
        if (addButton) {
          const trulyUnavailable = variant.available === false && variant.inventory_management;
          if (trulyUnavailable) {
            addButton.disabled = true;
            if (addButtonText) addButtonText.textContent = 'SOLD OUT';
          } else {
            addButton.disabled = false;
            if (addButtonText) addButtonText.textContent = 'ADD TO BAG';
          }
          setWaitlist(trulyUnavailable ? variant : null);
          const stickyBtn = document.querySelector('[data-mobile-add-trigger]');
          if (stickyBtn) stickyBtn.textContent = trulyUnavailable ? 'SOLD OUT' : 'ADD TO BAG';
        }

        // Stock note disabled — we don't surface inventory quantities to customers
        if (stockNote) {
          stockNote.hidden = true;
          stockNote.textContent = '';
        }
      } else {
        // No variant matches — disable add to bag
        if (addButton) {
          addButton.disabled = true;
          if (addButtonText) addButtonText.textContent = 'UNAVAILABLE';
        }
        setWaitlist(null);
      }
    }

    // Show/hide the back-in-stock waitlist for the selected variant, and
    // stamp the hidden tags input with product + variant context so the
    // signup is filterable in admin / actionable by a Flow or restock app.
    function setWaitlist(soldVariant) {
      const wl = document.querySelector('[data-waitlist]');
      if (!wl) return;
      // Don't fight the post-signup confirmation state
      if (wl.dataset.waitlistConfirmed === '1') return;

      if (!soldVariant) {
        wl.hidden = true;
        const panel = wl.querySelector('[data-waitlist-panel]');
        const toggle = wl.querySelector('[data-waitlist-toggle]');
        if (panel) panel.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        return;
      }

      wl.hidden = false;

      const selected = getSelectedOptions();
      const size = selected['Size'] || '';
      const color = selected['Color'] || '';

      const tagsInput = wl.querySelector('[data-waitlist-tags]');
      if (tagsInput) {
        const parts = ['waitlist'];
        const handle = wl.dataset.productHandle;
        if (handle) parts.push('waitlist-' + handle);
        if (size) parts.push('size:' + size);
        if (color) parts.push('color:' + color);
        if (soldVariant.id) parts.push('variant:' + soldVariant.id);
        tagsInput.value = parts.join(', ');
      }

      const ctx = wl.querySelector('[data-waitlist-context]');
      if (ctx) ctx.textContent = [color, size].filter(Boolean).join(' / ');
    }

    // Listen to option changes
    form.addEventListener('change', (e) => {
      if (e.target.matches('input[name^="options["]')) update();
    });

    // Size grid: clicking a <label> should check its input.
    // Native browser behavior handles this when label[for] matches input[id],
    // but we bind click as backup for any edge cases.
    form.addEventListener('click', (e) => {
      const label = e.target.closest('.size-option');
      if (label) {
        const id = label.getAttribute('for');
        if (id) {
          const input = document.getElementById(id);
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      const swatch = e.target.closest('.color-swatch');
      if (swatch) {
        const input = swatch.querySelector('input[type="radio"]');
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Run once on init to sync everything
    update();
  }

  // Re-init after DOM ready and on Shopify section reloads (theme editor)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductForm);
  } else {
    initProductForm();
  }
  document.addEventListener('shopify:section:load', initProductForm);

})();

/* ==========================================================================
   Mobile size picker sheet (PDP)
   ========================================================================== */
(function() {
  const $ = (sel) => document.querySelector(sel);

  const sheet = () => $('[data-size-sheet]');
  const form = () => $('[data-add-to-cart-form]');

  const openSheet = () => {
    const s = sheet();
    if (!s) return;
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-sheet-open');
  };

  const closeSheet = () => {
    const s = sheet();
    if (!s) return;
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-sheet-open');
  };

  document.addEventListener('click', async (e) => {
    // Open trigger
    if (e.target.closest('[data-mobile-add-trigger]')) {
      e.preventDefault();
      // If no size options on this product, fall back to direct submit
      if (!sheet()) {
        const submit = form()?.querySelector('[type="submit"]');
        if (submit) submit.click();
        return;
      }
      openSheet();
      return;
    }

    // Close trigger
    if (e.target.closest('[data-size-sheet-close]')) {
      e.preventDefault();
      closeSheet();
      return;
    }

    // Sold-out size tapped — select it and surface the waitlist instead of adding to cart
    const soldPick = e.target.closest('[data-size-pick].is-sold-out');
    if (soldPick) {
      e.preventDefault();
      const sizeValue = soldPick.dataset.sizePick;
      const radio = document.querySelector(`input[name="options[Size]"][value="${(window.CSS && CSS.escape) ? CSS.escape(sizeValue) : sizeValue}"]`);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeSheet();
      setTimeout(() => {
        const wl = document.querySelector('[data-waitlist]');
        if (!wl) return;
        wl.hidden = false;
        const panel = wl.querySelector('[data-waitlist-panel]');
        const toggle = wl.querySelector('[data-waitlist-toggle]');
        if (panel) panel.hidden = false;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        wl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = panel ? panel.querySelector('input[type="email"]') : null;
        if (input) input.focus({ preventScroll: true });
      }, 300);
      return;
    }

    // Size selection
    const pick = e.target.closest('[data-size-pick]:not(.is-sold-out)');
    if (pick) {
      e.preventDefault();
      const variantId = pick.dataset.variantId;
      const sizeValue = pick.dataset.sizePick;
      if (!variantId) return;

      // Visual feedback
      const note = pick.querySelector('.size-sheet__option-note');
      pick.style.opacity = '0.5';
      const original = note ? note.textContent : '';
      if (note) note.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }]
          })
        });
        if (!res.ok) throw new Error('Add failed');

        // Refresh and open cart drawer
        const cartRes = await fetch('/cart?section_id=cart-drawer');
        const text = await cartRes.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newDrawer = doc.querySelector('[data-drawer="cart"]');
        const oldDrawer = $('[data-drawer="cart"]');
        if (newDrawer && oldDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;

        // Update bag count
        const countRes = await fetch('/cart.js');
        const cart = await countRes.json();
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.toggleAttribute('hidden', cart.item_count === 0);
        });

        closeSheet();
        // Slight delay so the sheet closes before the drawer opens
        setTimeout(() => {
          const cartDrawer = $('[data-drawer="cart"]');
          if (cartDrawer) {
            cartDrawer.classList.add('is-open');
            $('#overlay')?.classList.add('is-active');
            document.body.classList.add('no-scroll');
          }
        }, 250);
      } catch (err) {
        console.error(err);
        if (note) note.textContent = original;
        pick.style.opacity = '1';
        alert('Could not add that piece. Please try again.');
      }
    }
  });

  // ESC to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sheet()?.classList.contains('is-open')) {
      closeSheet();
    }
  });
})();

/* ==========================================================================
   Favourites (local wishlist)
   Persists product IDs to localStorage. If a real wishlist app is installed
   later, this can be replaced by the app's API calls.
   ========================================================================== */
(function() {
  const KEY = 'arcaneguild_favourites';
  const get = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch (e) { return []; }
  };
  const save = (arr) => localStorage.setItem(KEY, JSON.stringify(arr));

  const updateButtonState = (btn) => {
    const productId = btn.dataset.productId || document.querySelector('[data-product-container]')?.querySelector('[data-product-json]') ? JSON.parse(document.querySelector('[data-product-json]').textContent).id : null;
    if (!productId) return;
    const favs = get();
    const isSaved = favs.includes(String(productId)) || favs.includes(productId);
    btn.classList.toggle('is-saved', isSaved);
    const textNode = btn.querySelector('[data-wishlist-text]');
    if (textNode) {
      textNode.textContent = isSaved ? 'SAVED' : 'SAVE TO FAVOURITES';
    }
  };

  // Init existing buttons
  document.querySelectorAll('[data-wishlist-add]').forEach(updateButtonState);

  // Handle clicks
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-add]');
    if (!btn) return;
    e.preventDefault();

    const container = document.querySelector('[data-product-container]');
    const jsonNode = container?.querySelector('[data-product-json]');
    if (!jsonNode) return;

    let product;
    try { product = JSON.parse(jsonNode.textContent); }
    catch (err) { return; }

    const productId = String(product.id);
    const favs = get();
    const idx = favs.indexOf(productId);

    if (idx === -1) {
      favs.push(productId);
      save(favs);
      btn.classList.add('is-saved');
      const textNode = btn.querySelector('[data-wishlist-text]');
      if (textNode) textNode.textContent = 'SAVED';
    } else {
      favs.splice(idx, 1);
      save(favs);
      btn.classList.remove('is-saved');
      const textNode = btn.querySelector('[data-wishlist-text]');
      if (textNode) textNode.textContent = 'SAVE TO FAVOURITES';
    }
  });
})();

/* ==========================================================================
   Size guide modal (PDP integration)
   ========================================================================== */
(function() {
  const modal = () => document.querySelector('[data-size-guide-modal]');

  // Pick the right chart for the current product based on type or tag.
  const pickChart = () => {
    const m = modal();
    if (!m) return;

    const jsonNode = document.querySelector('[data-product-json]');
    let productType = '';
    let productTags = [];
    if (jsonNode) {
      try {
        const data = JSON.parse(jsonNode.textContent);
        productType = (data.type || '').toLowerCase();
        productTags = (data.tags || []).map(t => t.toLowerCase());
      } catch (e) { /* fall through to default */ }
    }

    const charts = m.querySelectorAll('.size-guide-chart');
    let activated = false;

    // First pass: match by product type
    charts.forEach(c => {
      const matchType = (c.dataset.matchType || '').trim();
      if (!activated && matchType && productType && matchType === productType) {
        c.classList.add('is-active');
        activated = true;
      } else {
        c.classList.remove('is-active');
      }
    });

    // Second pass: match by tag if no type match
    if (!activated) {
      charts.forEach(c => {
        if (activated) return;
        const matchTags = (c.dataset.matchTag || '').split(',').map(t => t.trim()).filter(Boolean);
        if (matchTags.some(t => productTags.includes(t))) {
          c.classList.add('is-active');
          activated = true;
        }
      });
    }

    // Fallback: show the default (first) chart
    if (!activated) {
      const fallback = m.querySelector('.size-guide-chart[data-default]') || charts[0];
      if (fallback) fallback.classList.add('is-active');
    }
  };

  const open = () => {
    const m = modal();
    if (!m) return;
    pickChart();
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-guide-open');
  };

  const close = () => {
    const m = modal();
    if (!m) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-guide-open');
  };

  document.addEventListener('click', (e) => {
    // Trigger — any link with href="/pages/size-guide" or [data-size-guide-open]
    const trigger = e.target.closest('a[href$="/pages/size-guide"], a[href="/pages/size-guide"], [data-size-guide-open]');
    if (trigger) {
      e.preventDefault();
      if (modal()) {
        open();
      } else {
        console.warn('Size guide modal not found in DOM. Make sure size-guide-group is loaded.');
      }
      return;
    }

    if (e.target.closest('[data-size-guide-close]')) {
      e.preventDefault();
      close();
      return;
    }

    // Unit toggle
    const unitBtn = e.target.closest('[data-unit]');
    if (unitBtn) {
      const m = modal();
      if (!m) return;
      const unit = unitBtn.dataset.unit;
      m.querySelectorAll('[data-unit]').forEach(b => {
        const active = b.dataset.unit === unit;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      // Swap displayed values in all chart cells
      m.querySelectorAll('td[data-cm], td[data-in]').forEach(td => {
        td.textContent = unit === 'in' ? td.dataset.in : td.dataset.cm;
      });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal()?.classList.contains('is-open')) close();
  });
})();

/* ==========================================================================
   Locale picker (country / currency dropdown) — based on Dawn pattern
   ========================================================================== */
(function() {
  const root = () => document.querySelector('[data-locale-picker]');

  const closeMenu = () => {
    const r = root();
    if (!r) return;
    const menu = r.querySelector('[data-locale-menu]');
    const trigger = r.querySelector('[data-locale-toggle]');
    if (menu) menu.setAttribute('hidden', '');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    r.classList.remove('is-open');
  };

  const openMenu = () => {
    const r = root();
    if (!r) return;
    const menu = r.querySelector('[data-locale-menu]');
    const trigger = r.querySelector('[data-locale-toggle]');
    if (menu) menu.removeAttribute('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    r.classList.add('is-open');
  };

  document.addEventListener('click', (e) => {
    // Toggle dropdown
    if (e.target.closest('[data-locale-toggle]')) {
      e.preventDefault();
      const r = root();
      if (!r) return;
      const isOpen = r.classList.contains('is-open');
      if (isOpen) closeMenu(); else openMenu();
      return;
    }

    // Option selected
    const option = e.target.closest('[data-locale-option]');
    if (option) {
      e.preventDefault();
      const r = root();
      if (!r) return;
      const value = option.dataset.value;
      const input = r.querySelector('[data-locale-country-input]');
      const form = r.querySelector('form');
      if (!value || !input || !form) {
        console.warn('Locale picker: missing input, form, or value');
        return;
      }
      input.value = value;
      // Use requestSubmit if available (triggers form events + validation),
      // fall back to submit() for older browsers.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
      return;
    }

    // Click outside → close
    if (!e.target.closest('[data-locale-picker]')) {
      closeMenu();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });
})();

/* ==========================================================================
   Offer popup + floating tab
   Email capture via Shopify's native customer form. On success Shopify
   reloads with the form posted; we re-open the popup to the success view
   and reveal the discount code. Auto-open timing + frequency are config.
   ========================================================================== */
(function() {
  const popup = document.querySelector('[data-offer-popup]');
  if (!popup) return;

  const tab = document.querySelector('[data-offer-tab]');
  const delay = (parseFloat(popup.dataset.offerDelay) || 0) * 1000;
  const frequency = popup.dataset.offerFrequency || 'once';
  // Success is keyed on a unique URL param set via the form's return_to.
  // This avoids Shopify's quirk where any customer form submit trips every
  // form.posted_successfully? on the page (the waitlist uses the same form type).
  const params = new URLSearchParams(window.location.search);
  const justClaimed = params.get('offer_submitted') === '1';
  if (justClaimed && window.history && window.history.replaceState) {
    params.delete('offer_submitted');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
  }
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  const SEEN = 'arcaneguild_offer_seen';
  const CLAIMED = 'arcaneguild_offer_claimed';
  const TAB_HIDDEN = 'arcaneguild_offer_tab_hidden';

  const ls = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
  };
  const ss = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };

  const hasClaimed = () => !!ls.get(CLAIMED);

  const showView = (which) => {
    popup.querySelectorAll('[data-offer-capture], [data-offer-success]').forEach(v => {
      v.classList.remove('is-active');
    });
    const target = popup.querySelector(which === 'success' ? '[data-offer-success]' : '[data-offer-capture]');
    if (target) target.classList.add('is-active');
  };

  const open = () => {
    showView(hasClaimed() || justClaimed ? 'success' : 'capture');
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('offer-open');
    const focusable = popup.querySelector('.is-active input, .is-active button');
    if (focusable) setTimeout(() => focusable.focus(), 50);
  };

  const showTab = () => {
    if (!tab) return;
    if (ss.get(TAB_HIDDEN)) return;
    tab.hidden = false;
    requestAnimationFrame(() => tab.classList.add('is-visible'));
  };

  const close = () => {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('offer-open');
    // Mark as seen so it won't auto-open again per the frequency rule
    if (!designMode) {
      if (frequency === 'once') ls.set(SEEN, '1');
      if (frequency === 'session') ss.set(SEEN, '1');
    }
    showTab();
  };

  // Decide whether to auto-open on load
  const shouldAutoOpen = () => {
    if (justClaimed) return true;          // came back from a successful signup
    if (designMode) return true;           // always preview in the theme editor
    if (frequency === 'always') return true;
    if (frequency === 'session') return !ss.get(SEEN);
    return !ls.get(SEEN);                  // 'once'
  };

  // If the visitor already claimed, never auto-open the capture form,
  // but keep the tab available so they can retrieve their code.
  if (hasClaimed() && !justClaimed) {
    showTab();
  } else if (shouldAutoOpen()) {
    setTimeout(open, delay);
  } else {
    showTab();
  }

  // Record the claim once the success view is shown after a real signup
  if (justClaimed && !designMode) {
    const codeEl = popup.querySelector('[data-offer-code]');
    ls.set(CLAIMED, codeEl ? (codeEl.dataset.code || '1') : '1');
    ls.set(SEEN, '1');
  }

  // Delegated clicks
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-offer-open]')) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-offer-close]')) { e.preventDefault(); close(); return; }

    if (e.target.closest('[data-offer-tab-close]')) {
      e.preventDefault();
      if (tab) { tab.classList.remove('is-visible'); setTimeout(() => { tab.hidden = true; }, 300); }
      ss.set(TAB_HIDDEN, '1');
      return;
    }

    // Copy the discount code
    const codeBtn = e.target.closest('[data-offer-code]');
    if (codeBtn) {
      const code = codeBtn.dataset.code || '';
      const hint = codeBtn.querySelector('[data-offer-code-hint]');
      const done = () => { if (hint) { hint.textContent = 'Copied'; setTimeout(() => { hint.textContent = 'Tap to copy'; }, 1800); } };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(() => {});
      } else {
        const t = document.createElement('textarea');
        t.value = code; document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); done(); } catch (err) {}
        document.body.removeChild(t);
      }
      return;
    }
  });

  // ESC to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
  });
})();

/* ==========================================================================
   Back-in-stock waitlist (PDP)
   Toggle reveals the email field; availability + tag context are handled in
   the product form module. After a successful signup Shopify redirects to
   ?restock_submitted=1 (via the form's return_to) and we show confirmation.
   ========================================================================== */
(function() {
  const wl = document.querySelector('[data-waitlist]');
  if (!wl) return;

  const toggle = wl.querySelector('[data-waitlist-toggle]');
  const panel = wl.querySelector('[data-waitlist-panel]');
  const confirm = wl.querySelector('[data-waitlist-confirm]');

  // Toggle the email field open/closed
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-waitlist-toggle]')) {
      e.preventDefault();
      if (!panel) return;
      const open = panel.hidden;
      panel.hidden = !open;
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        const input = panel.querySelector('input[type="email"]');
        if (input) input.focus();
      }
    }
  });

  // Post-signup confirmation: redirected back with ?restock_submitted=1
  const params = new URLSearchParams(window.location.search);
  if (params.get('restock_submitted') === '1') {
    wl.hidden = false;
    wl.dataset.waitlistConfirmed = '1';      // tells the product module to leave it alone
    if (toggle) toggle.hidden = true;
    if (panel) panel.hidden = true;
    if (confirm) confirm.hidden = false;
    // Bring it into view so the customer sees the confirmation
    setTimeout(() => wl.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    // Clean the URL so a refresh doesn't replay it
    if (window.history && window.history.replaceState) {
      params.delete('restock_submitted');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
    }
  }
})();
